import { createSignal, For, Show } from "solid-js";
import Text from "../../components/Text";
import BotSelector from "../../components/BotSelector";
import LocationPicker from "../../components/LocationPicker";
import { showConfirm, showDialog } from "../../components/Dialog";
import { useToast } from "../../components/MessageBox";
import { useTranslation } from "../../hooks/useTranslation";
import { STORAGE_ACTIONS } from "../../services/resourceActions";
import { useInventoryApi, type StorageBotAssignmentInfo, type StorageInfo } from "../../services/inventoryApi";
import { Citybuild } from "../../models/Citybuild";
import shared from "./storageShared.module.css";
import styles from "./StorageOverviewTab.module.css";

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

interface Props {
  storage: StorageInfo;
  assignedBots: StorageBotAssignmentInfo[];
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onStorageChanged: () => Promise<void>;
  onBotsChanged: () => Promise<void>;
  onDeleted: () => void;
}

export default function StorageOverviewTab(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inv = useInventoryApi();

  const [formName, setFormName] = createSignal(props.storage.name);
  const [formLocation, setFormLocation] = createSignal(props.storage.location);
  const [formCitybuild, setFormCitybuild] = createSignal(props.storage.citybuild);
  const [formReachThroughChests, setFormReachThroughChests] = createSignal(props.storage.reachThroughChests);
  const [formIgnoreLoreCodes, setFormIgnoreLoreCodes] = createSignal(props.storage.ignoreLoreCodes);
  const [saving, setSaving] = createSignal(false);
  const [assignBotPickerId, setAssignBotPickerId] = createSignal<string | null>(null);
  const [assignBotBusy, setAssignBotBusy] = createSignal(false);

  const may = (action: string) => props.storage.myActions.includes(action);
  const scannedPct = () => props.storage.chestCount
    ? Math.round((props.storage.scannedChestCount / props.storage.chestCount) * 100)
    : 0;

  function resetForm() {
    setFormName(props.storage.name);
    setFormLocation(props.storage.location);
    setFormCitybuild(props.storage.citybuild);
    setFormReachThroughChests(props.storage.reachThroughChests);
    setFormIgnoreLoreCodes(props.storage.ignoreLoreCodes);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await inv.updateStorage(props.storage.id, {
        name: formName().trim(),
        location: formLocation().trim(),
        citybuild: formCitybuild(),
        reachThroughChests: formReachThroughChests(),
        ignoreLoreCodes: formIgnoreLoreCodes(),
      });
      await props.onStorageChanged();
      props.onEditingChange(false);
      toast?.success(t("STORAGE_SAVED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_SAVE_FAILED"));
    } finally {
      setSaving(false);
    }
  }

  async function assignBot() {
    const botId = assignBotPickerId();
    if (!botId) return;
    if (props.assignedBots.some(a => a.botRegistryId === botId)) {
      toast?.error(t("STORAGE_BOT_ALREADY_ASSIGNED"));
      return;
    }
    setAssignBotBusy(true);
    try {
      await inv.assignStorageBot(props.storage.id, botId);
      await props.onBotsChanged();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_ASSIGN_BOT_FAILED"));
    } finally {
      setAssignBotBusy(false);
    }
  }

  async function unassignBot(bot: StorageBotAssignmentInfo) {
    if (!await showConfirm(t("STORAGE_REMOVE_BOT_CONFIRM", bot.minecraftUsername), true)) return;
    try {
      await inv.unassignStorageBot(props.storage.id, bot.botRegistryId);
      await props.onBotsChanged();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_REMOVE_BOT_FAILED"));
    }
  }

  async function handleResetChests() {
    if (!await showConfirm(t("STORAGE_RESET_CHESTS_CONFIRM"), true)) return;

    const confirmation = await showDialog({
      title: t("STORAGE_RESET_CONFIRM_TITLE"),
      description: t("STORAGE_RESET_CONFIRM_DESC", props.storage.name),
      fields: [{ key: "name", label: t("STORAGE_NAME"), type: "text", placeholder: props.storage.name }],
    });
    if (!confirmation || confirmation.name.trim() !== props.storage.name) return;

    try {
      await inv.deleteChests(props.storage.id);
      await props.onStorageChanged();
      toast?.success(t("STORAGE_RESET_DONE"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_RESET_FAILED"));
    }
  }

  async function handleDelete() {
    if (!await showConfirm(t("STORAGE_DELETE_CONFIRM", props.storage.name), true)) return;
    try {
      await inv.deleteStorage(props.storage.id);
      props.onDeleted();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_DELETE_FAILED"));
    }
  }

  return (
    <Show when={!props.editing} fallback={
      <form class={`fcard v ${styles.editForm}`} onSubmit={e => { e.preventDefault(); void handleSave(); }}>
        <label class="v">
          <span class={shared.fieldLabel}><Text val="STORAGE_NAME" /></span>
          <input type="text" value={formName()} onInput={e => setFormName(e.currentTarget.value)} />
        </label>
        <label class="v">
          <span class={shared.fieldLabel}><Text val="STORAGE_CITYBUILD" /></span>
          <select value={formCitybuild()} disabled={!!props.storage.sellChamberBotId} onChange={e => setFormCitybuild(parseInt(e.currentTarget.value))}>
            <For each={citybuildOptions}>{(key) => <option value={Citybuild[key]}>{key}</option>}</For>
          </select>
        </label>
        <label class="v">
          <span class={shared.fieldLabel}><Text val="STORAGE_LOCATION" /></span>
          <LocationPicker value={formLocation()} onChange={setFormLocation} optional disabled={!!props.storage.sellChamberBotId} />
          <Show when={props.storage.sellChamberBotId}>
            <span class={shared.mutedNote}>{t("STORAGE_SELL_CHAMBER_LOCATION_SYNCED", props.storage.sellChamberBotName)}</span>
          </Show>
        </label>
        <label class="v">
          <span class={shared.checkRow}>
            <input
              type="checkbox"
              checked={formReachThroughChests()}
              onChange={e => setFormReachThroughChests(e.currentTarget.checked)}
            />
            {" "}<Text val="STORAGE_REACH_THROUGH_CHESTS" />
          </span>
          <span class={shared.mutedNote}><Text val="STORAGE_REACH_THROUGH_CHESTS_HELP" /></span>
        </label>
        <label class="v">
          <span class={shared.checkRow}>
            <input
              type="checkbox"
              checked={formIgnoreLoreCodes()}
              onChange={e => setFormIgnoreLoreCodes(e.currentTarget.checked)}
            />
            {" "}<Text val="STORAGE_IGNORE_LORE_CODES" />
          </span>
          <span class={shared.mutedNote}><Text val="STORAGE_IGNORE_LORE_CODES_HELP" /></span>
        </label>
        <div class="h" style={{ "justify-content": "flex-end", gap: "var(--spacing-sm)" }}>
          <button type="button" class="btn btn-secondary" onClick={() => { resetForm(); props.onEditingChange(false); }}>
            <Text val="CANCEL" />
          </button>
          <button type="submit" class="btn btn-primary" disabled={saving()}>
            <Show when={saving()} fallback={<Text val="SAVE" />}><i class="fa-solid fa-spinner fa-spin" /></Show>
          </button>
        </div>
      </form>
    }>
      <div class={styles.statGrid}>
        <div class={styles.stat}>
          <div class={styles.statValue}>{props.storage.chestCount}</div>
          <div class={styles.statLabel}><Text val="STORAGE_STAT_TOTAL" /></div>
        </div>
        <div class={`${styles.stat} ${styles.statBlue}`}>
          <div class={styles.statValue}>{props.storage.scannedChestCount}</div>
          <div class={styles.statLabel}><Text val="STORAGE_STAT_SCANNED_COUNT" /></div>
        </div>
        <div class={`${styles.stat} ${styles.statPurple}`}>
          <div class={styles.statValue}>{props.storage.chestCount - props.storage.scannedChestCount}</div>
          <div class={styles.statLabel}><Text val="STORAGE_FILTER_NEVER_SCANNED" /></div>
        </div>
        <div class={`${styles.stat} ${styles.statGreen}`}>
          <div class={styles.statValue}>{scannedPct()}%</div>
          <div class={styles.statLabel}><Text val="STORAGE_STAT_SCANNED" /></div>
        </div>
      </div>

      <div class={styles.metaCard}>
        <div class={styles.metaRow}>
          <span class={styles.metaKey}><Text val="STORAGE_NAME" /></span>
          <span class={styles.metaVal}>{props.storage.name}</span>
        </div>
        <div class={styles.metaRow}>
          <span class={styles.metaKey}><Text val="STORAGE_CITYBUILD" /></span>
          <span class={styles.metaVal}>{props.storage.citybuildName}</span>
        </div>
        <div class={styles.metaRow}>
          <span class={styles.metaKey}><Text val="STORAGE_LOCATION" /></span>
          <span class={styles.metaVal}>{props.storage.location || "-"}</span>
        </div>
        <div class={styles.metaRow}>
          <span class={styles.metaKey}><Text val="STORAGE_OWNED_BY" /></span>
          <span class={styles.metaVal}>{props.storage.ownerName}</span>
        </div>
      </div>

      <div class={shared.sectionTitle}><Text val="STORAGE_ASSIGNED_BOTS" /></div>
      <div class={styles.botRow}>
        <Show when={props.assignedBots.length === 0}>
          <span class={shared.mutedNote}><Text val="STORAGE_NO_BOTS_ASSIGNED" /></span>
        </Show>
        <For each={props.assignedBots}>
          {(bot) => (
            <span class={styles.botChip}>
              <img src={`https://mc-heads.net/avatar/${bot.minecraftUuid}/22`} alt="" />
              <span>{bot.minecraftUsername}</span>
              <Show when={may(STORAGE_ACTIONS.BotsAssign) && bot.botRegistryId !== props.storage.sellChamberBotId}>
                <button class={styles.botChipRemove} title={t("DELETE")} onClick={() => void unassignBot(bot)}>
                  <i class="fa-solid fa-xmark" />
                </button>
              </Show>
            </span>
          )}
        </For>
      </div>

      <Show when={may(STORAGE_ACTIONS.BotsAssign)}>
        <div class={styles.inlineForm}>
          <BotSelector storageKey="storage_assign_bot" onSelect={(id) => setAssignBotPickerId(id)} />
          <button
            class="btn btn-secondary btn-sm"
            disabled={!assignBotPickerId() || assignBotBusy()}
            onClick={() => void assignBot()}
          >
            <Show when={!assignBotBusy()} fallback={<><i class="fa-solid fa-spinner fa-spin" /> <Text val="STORAGE_ADDING" /></>}>
              <i class="fa-solid fa-plus" /> <Text val="STORAGE_ASSIGN" />
            </Show>
          </button>
        </div>
      </Show>

      <Show when={may(STORAGE_ACTIONS.Delete)}>
        <div class={styles.dangerZone}>
          <div class={styles.dangerTitle}><Text val="STORAGE_DANGER_ZONE" /></div>
          <div class={styles.dangerRow}>
            <div class={styles.dangerDesc}><Text val="STORAGE_RESET_CHESTS_DESC" /></div>
            <button class="btn btn-danger btn-sm" onClick={() => void handleResetChests()}>
              <i class="fa-solid fa-trash-can" /> <Text val="STORAGE_RESET_CHESTS" />
            </button>
          </div>
          <div class={styles.dangerRow}>
            <div class={styles.dangerDesc}>
              <Show when={props.storage.sellChamberBotId} fallback={<Text val="STORAGE_DELETE_DESC" />}>
                {t("STORAGE_SELL_CHAMBER_DELETE_LOCKED", props.storage.sellChamberBotName)}
              </Show>
            </div>
            <button class="btn btn-danger btn-sm" disabled={!!props.storage.sellChamberBotId} onClick={() => void handleDelete()}>
              <i class="fa-solid fa-trash" /> <Text val="STORAGE_DELETE" />
            </button>
          </div>
        </div>
      </Show>
    </Show>
  );
}
